import Tool, {
  type IToolActionInput,
  type IToolAvailableInput,
  type IToolEnabledInput,
  simpleToolButton,
} from './Tool';
import {
  hasRecordReference,
  isConnector,
  isDependencyConnector,
  isLinkableRecordShape,
} from '../objects/guards';
import type Connector from '../objects/Connector';
import { type Attachment } from '../objects/Connector';

class ToolCreateRecordLink extends Tool {
  static toolName = 'Create dependency';

  static available({ selection, controller }: IToolAvailableInput) {
    if (!controller.recordLinkManager) {
      return false;
    }

    const objects = selection?.getObjects();

    if (
      !objects?.every(
        (object: fabric.Object) =>
          isConnector(object) && !isDependencyConnector(object)
      )
    ) {
      return false;
    }

    return objects.every((object: Connector) => {
      const attachments = object.attachments;
      return (
        attachments.length === 2 &&
        attachments.every(
          (attachment): attachment is Attachment =>
            !!(attachment?.object && isLinkableRecordShape(attachment.object))
        ) &&
        attachments[0].object.recordId !== attachments[1].object.recordId
      );
    });
  }

  static action({ controller, selection }: IToolActionInput) {
    const connectors = selection.getObjects();

    // available method checks this already, but adding redundant check just in case
    if (
      !connectors.every(connector => {
        if (!isConnector(connector)) {
          return false;
        }
        const attachments = connector.attachments;
        return (
          attachments.length === 2 &&
          attachments.every(attachment =>
            isLinkableRecordShape(attachment?.object)
          )
        );
      })
    ) {
      return false;
    }

    const recordLinkItems = connectors
      .map((connector: Connector) => {
        const attachments = connector.attachments;
        const parent = attachments[0]?.object;
        const child = attachments[1]?.object;
        if (hasRecordReference(parent) && hasRecordReference(child)) {
          return {
            parentId: parent.recordId,
            parentType: parent.recordType,
            childId: child.recordId,
            childType: child.recordType,
            connector,
          };
        }
        return null;
      })
      .filter(Boolean);

    controller.actionSetRecordLinkItems(recordLinkItems);

    controller.actionShowObjectToolbar();
    this.toolActionComplete({ controller });
  }

  static enabled({ selection, controller }: IToolEnabledInput) {
    return this.available({ selection, controller });
  }

  static buttonComponent() {
    return simpleToolButton(
      ToolCreateRecordLink,
      'fa-regular fa-diagram-nested'
    );
  }
}

export default ToolCreateRecordLink;
